import { useLayoutEffect, useState } from "react"

import { HEADER_HEIGHT_VARIABLE_NAME } from "shared/constants/app-constants"
import { useElementHeightStore } from "shared/lib/css/useElementHeightStore"

export function useActiveAnchor(hrefs: string[]): string | null {
  const [activeHref, setActiveHref] = useState<string | null>(null)
  const headerHeight = useElementHeightStore(HEADER_HEIGHT_VARIABLE_NAME)

  useLayoutEffect(() => {
    if (typeof window === "undefined" || hrefs.length === 0) return undefined

    const elements =
      hrefs
        .map(href => ({
          href,
          el: document.getElementById(href.replace("#", ""))
        }))
        .filter(
          (item): item is { href: string; el: HTMLElement } => item.el !== null
        ) || []

    const rootMargin = `-${headerHeight + 80}px 0px -50% 0px`

    if (elements.length === 0) return undefined

    const intersectingSet = new Set<string>()

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const href = `#${entry.target.id}`
          if (entry.isIntersecting) {
            intersectingSet.add(href)
          } else {
            intersectingSet.delete(href)
          }
        })

        let topmost: string | null = null
        let topmostTop = Infinity

        // eslint-disable-next-line no-restricted-syntax
        for (const { href, el } of elements) {
          if (intersectingSet.has(href)) {
            const { top } = el.getBoundingClientRect()
            if (top < topmostTop) {
              topmostTop = top
              topmost = href
            }
          }
        }

        if (topmost !== null) {
          setActiveHref(topmost)
        }
      },
      { rootMargin }
    )

    elements.forEach(({ el }) => observer.observe(el))

    return () => observer.disconnect()
  }, [hrefs, headerHeight])

  return activeHref
}
